import { useStore } from '../../store';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import { useCallback } from 'react';
import { DUPLICATE_ERROR_CODES, NOT_NULL_ERROR_CODE } from './duplicateErrorMap';
import { alreadyShownAsToast } from './errorToastGuard';

export interface ApiErrorResponse {
  errorCode: string;
  message: string;
  details?: string;
  timestamp?: string;
  path?: string;
  additionalData?: Record<string, any>;
}

export class ApiError extends Error {
  public status: number;
  public errorCode: string;
  public details?: string;
  public additionalData?: Record<string, any>;

  constructor(message: string, status: number, errorCode: string, details?: string, additionalData?: Record<string, any>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errorCode = errorCode;
    this.details = details;
    this.additionalData = additionalData;
  }
}

export const useErrorHandler = () => {
  const logout = useStore((state) => state.logout);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const handleApiError = useCallback((error: ApiError) => {
    // Duplicate-key and not-null errors are rendered by the central
    // services/ErrorHandler.ts so we don't show a second toast here.
    if (DUPLICATE_ERROR_CODES[error.errorCode] || error.errorCode === NOT_NULL_ERROR_CODE) {
      return;
    }

    switch (error.errorCode) {
      case 'AUTH_001': // UNAUTHORIZED
      case 'AUTH_004': // TOKEN_EXPIRED
      case 'AUTH_005': // INVALID_TOKEN
        showToast('error', 'Authentication Error', 'Your session has expired. Please log in again.');
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 2000);
        break;

      case 'AUTH_002': // FORBIDDEN
        showToast('error', 'Permission Denied', 'You do not have permission to perform this action.');
        break;

      case 'USER_002': // USERNAME_ALREADY_EXISTS
        showToast('error', 'Username Already Exists', 'A user with this username already exists. Please choose a different username.');
        break;

      case 'USER_003': // EMAIL_ALREADY_EXISTS
        showToast('error', 'Email Already Exists', 'A user with this email already exists. Please choose a different email.');
        break;

      case 'USER_001': // USER_NOT_FOUND
        showToast('error', 'User Not Found', 'The requested user could not be found.');
        break;

      case 'TENANT_001': // TENANT_NOT_FOUND
        showToast('error', 'Tenant Not Found', 'The requested tenant could not be found.');
        break;

      case 'ROLE_001': // ROLE_NOT_FOUND
        showToast('error', 'Role Not Found', 'The requested role could not be found.');
        break;

      case 'VAL_001': // VALIDATION_ERROR
        showToast('error', 'Validation Error', error.details || 'Please check your input and try again.');
        break;

      case 'DB_002': // CONSTRAINT_VIOLATION
      case 'DB_003': // DUPLICATE_KEY
        // Show more detailed error message if available
        const errorMessage = error.details || 'The data you entered conflicts with existing data. Please check your input.';
        showToast('error', 'Data Error', errorMessage);
        break;

      default:
        showToast('error', 'Error', error.message || 'An unexpected error occurred.');
        break;
    }
  }, [showToast, logout, navigate]);

  const handleStructuredError = useCallback((error: ApiErrorResponse) => {
    const apiError = new ApiError(
      error.message,
      400, // Default status
      error.errorCode,
      error.details,
      error.additionalData
    );
    handleApiError(apiError);
  }, [handleApiError]);

  const handleHttpError = useCallback((error: any) => {
    switch (error.status) {
      case 401:
        showToast('error', 'Authentication Error', 'Your session has expired. Please log in again.');
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 2000);
        break;

      case 400:
        // Check if it's an authentication-related 400 error
        const errorData = error.response?.data;
        if (errorData?.errorCode === 'AUTH_001' || 
            errorData?.message?.includes('User not authenticated') ||
            errorData?.message?.includes('Authentication failed')) {
          showToast('error', 'Authentication Error', 'Your session has expired. Please log in again.');
          setTimeout(() => {
            logout();
            navigate('/login');
          }, 2000);
        } else {
          showToast('error', 'Bad Request', error.message || 'Invalid request data.');
        }
        break;

      case 403:
        showToast('error', 'Permission Denied', 'You do not have permission to perform this action.');
        break;

      case 404:
        showToast('error', 'Not Found', 'The requested resource could not be found.');
        break;

      case 409:
        showToast('error', 'Conflict', 'The data you entered conflicts with existing data.');
        break;

      case 422:
        showToast('error', 'Validation Error', 'Please check your input and try again.');
        break;

      case 500:
        showToast('error', 'Server Error', 'An internal server error occurred. Please try again later.');
        break;

      default:
        showToast('error', 'Error', error.message || 'An unexpected error occurred.');
        break;
    }
  }, [showToast, logout, navigate]);

  const handleGenericError = useCallback((error: any) => {
    const message = error?.message || 'An unexpected error occurred.';
    
    // Check for specific error patterns
    if (message.includes('User not authenticated') || 
        message.includes('Authentication failed') ||
        message.includes('User not found in request context')) {
      showToast('error', 'Authentication Error', 'Your session has expired. Please log in again.');
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
    } else if (message.includes('Username already exists')) {
      showToast('error', 'Username Already Exists', 'A user with this username already exists. Please choose a different username.');
    } else if (message.includes('permission') || message.includes('authorized')) {
      showToast('error', 'Permission Denied', 'You do not have permission to perform this action.');
    } else if (message.includes('network') || message.includes('fetch')) {
      showToast('error', 'Network Error', 'Unable to connect to the server. Please check your internet connection.');
    } else {
      showToast('error', 'Error', message);
    }
  }, [showToast, logout, navigate]);

  const handleError = useCallback((error: any, context?: string) => {
    console.error(`Error in ${context || 'operation'}:`, error);

    // Prevent duplicate toasts when the same (re-thrown) error has already been shown as a toast
    // by another level (for example the enclosing CrudPage). The logging above stays.
    if (alreadyShownAsToast(error)) return;

    // Handle ApiError instances (from this module)
    if (error instanceof ApiError) {
      handleApiError(error);
      return;
    }

    // Handle API client errors (e.g. OpenAPI fetch) that have response body with errorCode/message
    if (error?.body && typeof error.body === 'object' && (error.body.errorCode != null || error.body.message)) {
      handleStructuredError({
        errorCode: error.body.errorCode,
        message: error.body.message,
        details: error.body.details,
        ...error.body
      });
      return;
    }

    // Handle error objects with errorCode (from our backend)
    if (error?.errorCode) {
      handleStructuredError(error);
      return;
    }

    // Handle HTTP status codes
    if (error?.status) {
      handleHttpError(error);
      return;
    }

    // Handle generic errors
    handleGenericError(error);
  }, [handleApiError, handleStructuredError, handleHttpError, handleGenericError]);

  return { handleError };
}; 